import type { DocumentResource, FormWidgetData, VideoResource, VideoResourceWithDocs } from "../../widgetDataTs.js";
import type { FormTrackingName } from "../form-settings/form-experience/form-experience-settings.js";
import { isString, type Nil, type PartialWithNil } from "@mcwd/typescript-type-guards";
import type { MaybeRefOrGetter } from "vue";
import { toValue } from "vue";
import type { HiddenMarketoInputType, MarketoVals, UserMarketoInputName } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import type { TagManagerEvent } from "../../tracking/TagManager/Wrapper/TagManagerWrapper.js";
import { useFormLogger } from "../composables/use-form-logger.js";
import type { ILogger } from "../../utils/Logger/ILogger.js";
import type { GatingState } from "../types/definitions/gating/gating-state.js";
import { useFormSettings } from "../composables/use-form-settings.js";
import type { IFormSettings } from "../form-settings/form-settings-factory.js";
import { CtaFactory, FormCtaDataManager, type CtaData } from "./cta-tracking/index.js";
import { ValueGetters } from "../../tracking/data/ValueGetters.js";
import type { MarketoKnownUserStatus } from "../../tracking/data/functions/getInitialMarketoKnownUserStatus.js";
import { getPreviousPageCookie } from "../../tracking/page-data/PreviousPageUrlCookie.js";
/**
 * 
 *   - FormModalOpen:  Triggers only when the form is in a modal. Triggers when the modal first opens. 
 *   - FormStart:  Triggers when a user first interacts with a form field.
 *   - FormComplete:  Triggers when a regular form is completed successfully
 *   - UngatedFormComplete:  Triggers when a blind form is completed successfully
 *   - FormError:  Triggers when a form validation error occurs.
 */
const FormEvents = [
  "FormModalOpen",
  "FormStart",
  "FormComplete",
  "UngatedFormComplete",
  "FormError"
] as const;
type FormEvents = typeof FormEvents[number];

interface FormEventObject extends TagManagerEvent {
  event: FormEvents;
  formName: FormTrackingName;
  widgetName: string;
  widgetVariation: string;
  gatingState: GatingState.MarketoForm;
  videoId?: string | Nil;
  videoTitle?: string | Nil;
  documentIds?: string[] | Nil;
  documentTitles?: string[] | Nil;
  offerId?: string | Nil;
  tacticId?: string | Nil;
  InitialMarketoKnownUserStatus: MarketoKnownUserStatus;
  previousPage:string | Nil;
}
declare const Munchkin: any;
export class FormTrackingManager {
  formTrackingName: FormTrackingName;
  hiddenFieldValuesObject: MaybeRefOrGetter<PartialWithNil<HiddenMarketoInputType>>;
  userInputValues: MaybeRefOrGetter<Partial<{ [K in UserMarketoInputName]: MarketoVals[K] }>>;
  isInModal: MaybeRefOrGetter<boolean>;
  widgetData: MaybeRefOrGetter<FormWidgetData>;
  gatingState: MaybeRefOrGetter<GatingState.MarketoForm>;
  formSettings: IFormSettings;
  formCtaDataManager: FormCtaDataManager;

  #logger: ILogger;
  constructor({
    formTrackingName,
    hiddenFieldValuesObject,
    userInputValues,
    isInModal,
    widgetData,
    gatingState,
  }: {
    formTrackingName: FormTrackingName;
    hiddenFieldValuesObject: MaybeRefOrGetter<PartialWithNil<HiddenMarketoInputType>>;
    userInputValues: MaybeRefOrGetter<Partial<{ [K in UserMarketoInputName]: MarketoVals[K] }>>,
    isInModal?: MaybeRefOrGetter<boolean> | Nil;
    widgetData: MaybeRefOrGetter<FormWidgetData>;
    gatingState: MaybeRefOrGetter<GatingState.MarketoForm>;
  }) {
    const { logger } = useFormLogger();
    const { FormSettings } = useFormSettings();
    this.#logger = logger;
    this.formSettings = FormSettings;
    this.isInModal = isInModal ?? false;
    this.formTrackingName = formTrackingName;
    this.widgetData = widgetData;
    this.hiddenFieldValuesObject = hiddenFieldValuesObject;
    this.userInputValues = userInputValues;
    this.gatingState = gatingState;
    this.formCtaDataManager = this.#createCtaDataManager(widgetData);
  }
  #createCtaDataManager(widgetData: MaybeRefOrGetter<FormWidgetData>): FormCtaDataManager {
    const tracking = toValue(widgetData)?.setup?.tracking;
    const CtaClassObject = CtaFactory.createCtaClass(tracking.ctaElement, tracking.ctaType);
    return new FormCtaDataManager(tracking, CtaClassObject);
  }
  get setupForm() {
    return toValue(this.widgetData).setup.form;
  }

  get formName() {
    return this.formTrackingName;
  }

  // widget name, which defaults to the form name when not provided
  get widgetName() {
    return toValue(this.widgetData).widget.name ?? this.formTrackingName;
  }

  get widgetVariation() {
    return toValue(this.widgetData).widget.variation ?? "";
  }

  get video() {
    return "video" in this.setupForm
      ? (this.setupForm.video.selected as VideoResource | VideoResourceWithDocs)
      : null;
  }

  get documents() {
    if (("document" in this.setupForm)) {
      return (this.setupForm.document.selected ?? null) as DocumentResource[] | null;
    }
    else {
      return null;
    }
  }
  get offerId() {
    return toValue(this.widgetData)?.setup?.tracking?.offerId ?? null;
  }
  get widgetType() {
    return toValue(this.widgetData).widget.variation ?? null;
  }
  get ctaData(): CtaData {
    try {
      return this.formCtaDataManager.AnalyticData;
    } catch (error) {
      this.#logger.error(error);
      return {
        ctaText: "",
        ctaType: null,
        ctaClass: "",
      };
    }
  }

  #createEventObject(event: FormEvents): FormEventObject {
    const { video, documents, offerId } = this;
    const hiddenFields = toValue(this.hiddenFieldValuesObject);
    const tacticId = hiddenFields?.Campaign__c ?? null;
    const eventObj: FormEventObject = {
      event,
      formName: this.formName,
      widgetName: this.widgetName,
      widgetVariation: this.widgetVariation,
      gatingState: toValue(this.gatingState),
      videoId: video?.legacyId,
      videoTitle: video?.title,
      documentIds: documents?.map((d) => d.legacyId),
      documentTitles: documents?.map(d => d.title),
      offerId,
      tacticId,
      InitialMarketoKnownUserStatus: ValueGetters.InitialMarketoKnownUserStatus.value,
      previousPage: getPreviousPageCookie(),
    };

    if (event === "FormComplete") {
      return {
        ...eventObj,
        ...this.#getEnhancedConversionDataForGoogleAds()
      };
    }
    if (event === "FormModalOpen") {
      return {
        ...eventObj,
        ...this.ctaData
      };
    }
    return eventObj;
  }

  sendEvent(eventName: FormEvents) {
    this.#logger.log("About to send event: ", eventName);
    window.McTagManager.sendEvent(this.#createEventObject(eventName));
  }

  #getEnhancedConversionDataForGoogleAds() {
    const userInputValues = toValue(this.userInputValues);
    let { Email, Phone } = userInputValues;
    Email = Email?.trim();
    Phone = Phone?.trim();
    if (isString(Email) && Email.length > 0 && isString(Phone) && Phone.length > 0) {
      return {
        leadsUserData: {
          email: Email,
          phone_number: Phone
        }
      };
    }
    return {};
  }

  #canSendToMunchikin() {
    const isDocumentOrVideo = this.formSettings.formSettingsKey === "document" || this.formSettings.formSettingsKey === "video";
    if (!isDocumentOrVideo)
      return false;
    const doesFormHaveDocuments = this.documents != null && this.documents.length > 0;
    if (!doesFormHaveDocuments)
      return false;
    const canWidgetSendOfferId = toValue(this.widgetData).setup.tracking.usePageOfferId === true;
    if (!canWidgetSendOfferId)
      return false;

    return true;
  }
  #onAnyFormComplete() {
    if (this.#canSendToMunchikin()) {
      const offerIds = this.documents!.map(d => d.salesforceOfferId);
      try {
        if (typeof Munchkin === 'undefined') {
          throw new Error("Munchkin code did not load in.");
        }
        for (const id of offerIds) {
          if (id) {
            Munchkin.munchkinFunction('clickLink', {
              'href': `https://www.mastercontrol.com/multi-doc-selector/${id}`
            });
          }
        }
      } catch (error) {
        this.#logger.error(error);
      }
    }
  }
  #formOpenHasTriggered = false;
  FormOpen() {
    // Prevent form open tracking from triggering more than once
    if (this.#formOpenHasTriggered !== true) {
      this.#formOpenHasTriggered = true;
      if (this.isInModal) {
        this.sendEvent("FormModalOpen");
      }
    }
  }

  #formUserStartHasTriggered = false;
  FormUserStart() {
    // Prevent form user start tracking from triggering more than once
    if (this.#formUserStartHasTriggered !== true) {
      this.#formUserStartHasTriggered = true;
      this.sendEvent("FormStart");
    }
  }

  #formCompleteHasTriggered = false;
  FormComplete() {
    // Prevent form complete tracking from triggering more than once
    if (this.#formCompleteHasTriggered !== true) {
      // If the form is prefilled, and a user just presses the submit button, it may not fire the start event, so handle it here before completion
      if (this.#formUserStartHasTriggered !== true) {
        this.FormUserStart();
      }
      this.#formCompleteHasTriggered = true;
      //should only trigger for multi doc selectors and video carousel
      this.#onAnyFormComplete();
      this.sendEvent("FormComplete");
    }
  }

  #ungatedFormCompleteHasTriggered = false;
  /** Ungated Form Complete (aka blind submit) */
  UngatedFormComplete() {
    // Prevent ungated form complete tracking from triggering more than once
    if (this.#ungatedFormCompleteHasTriggered !== true) {
      this.#ungatedFormCompleteHasTriggered = true;
      this.#onAnyFormComplete();
      this.sendEvent("UngatedFormComplete");
    }
  }

  #formValidationErrorHasTriggered = false;
  FormValidationError() {
    // Prevent form validation error tracking from triggering more than once
    if (this.#formValidationErrorHasTriggered !== true) {
      this.#formValidationErrorHasTriggered = true;
      this.sendEvent("FormError");
    }
  }
}
